import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, ArrowLeft } from 'lucide-react';
import { PublicHeader } from '@/components/Layout/PublicHeader';
import { PublicFooter } from '@/components/Layout/PublicFooter';
import { HelpSearchBar, HelpBreadcrumb } from '@/components/help';
import { helpService, SearchResult, stripMarkdown } from '@/services/helpService';
import { Spinner } from '@/components/ui/Spinner';

export function HelpSearch() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';

  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    async function search() {
      if (!query) {
        setResults([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const data = await helpService.search(query, 20);
        setResults(data.results);
        setTotal(data.total);
      } catch (error) {
        console.error('Search failed:', error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }
    search();
  }, [query]);

  const pageTitle = query ? `Search: "${query}" | Help Center | Cascady` : 'Search | Help Center | Cascady';

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={`Search the Cascady Help Center${query ? ` for "${query}"` : ''}. Find answers to your questions about AI image and video generation.`} />
        <meta name="robots" content="noindex" />
      </Helmet>
      <PublicHeader />

      <div className="container mx-auto px-4 max-w-4xl py-8">
        {/* Breadcrumb */}
        <HelpBreadcrumb items={[{ label: 'Search Results' }]} />

        {/* Search Bar */}
        <div className="mb-8">
          <HelpSearchBar initialQuery={query} />
        </div>

        {/* Results Header */}
        <div className="mb-6">
          {isLoading ? (
            <p className="text-gray-500">Searching...</p>
          ) : query ? (
            <p className="text-gray-600">
              <span className="font-medium text-gray-900">{total}</span>{' '}
              {total === 1 ? 'result' : 'results'} for{' '}
              <span className="font-medium text-gray-900">"{query}"</span>
            </p>
          ) : (
            <p className="text-gray-500">Enter a search term to find help articles</p>
          )}
        </div>

        {/* Results */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : results.length > 0 ? (
          <div className="space-y-4">
            {results.map((result) => (
              <Link
                key={result.slug}
                to={`/help/${result.category}/${result.slug}`}
                className="block"
              >
                <div className="p-5 rounded-xl border border-gray-200 bg-white hover:border-violet-300 hover:shadow-sm transition-all">
                  <h3 className="font-medium text-gray-900 mb-2">
                    {stripMarkdown(result.title)}
                  </h3>
                  <p className="text-sm text-gray-500 line-clamp-2 mb-3">
                    {stripMarkdown(result.excerpt)}
                  </p>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-violet-600 bg-violet-100 px-2 py-0.5 rounded-full">
                      {result.category_name}
                    </span>
                    <span className="text-xs text-gray-400">
                      {result.similarity}% match
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : query ? (
          <div className="text-center py-12 bg-gray-50 rounded-2xl">
            <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No results found
            </h3>
            <p className="text-gray-500 mb-6">
              We couldn't find any articles matching "{query}"
            </p>
            <div className="space-y-2">
              <p className="text-sm text-gray-500">Try:</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>Using different keywords</li>
                <li>Checking your spelling</li>
                <li>Using more general terms</li>
              </ul>
            </div>
            <Link
              to="/help"
              className="inline-flex items-center gap-2 mt-6 text-violet-600 hover:underline"
            >
              <ArrowLeft className="w-4 h-4" />
              Browse all categories
            </Link>
          </div>
        ) : null}
      </div>

      <PublicFooter />
    </div>
  );
}
